import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";

const WalletTransactions = ({ user, onLogout }) => {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  
  const [transactions, setTransactions] = useState([]);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });

  useEffect(() => {
    fetchData();
  }, [sellerId, currentPage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch seller
      const sellerRes = await axios.get(`${API}/sellers/${sellerId}`);
      setSeller(sellerRes.data);
      
      // Fetch transactions with pagination (backend sorts newest to oldest)
      const txnRes = await axios.get(`${API}/wallet/admin/${sellerId}/transactions?page=${currentPage}&limit=50`);
      setTransactions(txnRes.data.transactions);
      setPagination(txnRes.data.pagination);
      
      setLoading(false);
    } catch (error) {
      console.error("Failed to load data:", error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout user={user} onLogout={onLogout}>
        <div className="flex justify-center py-12">
          <p>Loading transactions...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate(`/admin/sellers/${sellerId}/wallet`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Wallet
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Wallet Transactions</h1>
              <p className="text-slate-600">{seller?.brand_name || seller?.company_registered_name}</p>
            </div>
          </div>
        </div>

        {/* Current Balance */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Current Wallet Balance</p>
                <p className={`text-3xl font-bold ${seller?.wallet_balance < 0 ? 'text-red-600' : 'text-[#00A699]'}`}>
                  ₹{(seller?.wallet_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-600">Total Transactions</p>
                <p className="text-2xl font-bold text-slate-900">{transactions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle>Transaction History</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {transactions.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Order/LRN</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Amount</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Balance After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((txn) => (
                      <tr key={txn.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 align-middle text-sm text-slate-900">
                          <div className="whitespace-nowrap">
                            <div>{formatToIST(txn.created_at, false)}</div>
                            <div className="text-xs text-slate-500">{formatToIST(txn.created_at).split(', ')[1]}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${
                            txn.type === 'credit' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {txn.type === 'credit' ? '+ Credit' : '- Debit'}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-700">
                          {txn.description}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm">
                          {txn.order_number ? (
                            <div className="flex flex-col gap-0.5">
                              <button
                                onClick={() => navigate(`/admin/orders/${txn.order_id}`)}
                                className="text-[#00A699] hover:underline font-medium text-left"
                              >
                                {txn.order_number}
                              </button>
                              {txn.lrn && (
                                <button
                                  onClick={() => navigate(`/admin/orders/${txn.order_id}`)}
                                  className="text-slate-500 hover:text-[#00A699] hover:underline text-xs text-left transition-colors"
                                >
                                  LRN: {txn.lrn}
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </td>
                        <td className={`px-4 py-3 align-middle text-sm font-medium text-right ${
                          txn.type === 'credit' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {txn.type === 'credit' ? '+' : '-'} ₹{txn.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className={`px-4 py-3 align-middle text-sm font-medium text-right ${
                          txn.balance_after < 0 ? 'text-red-600' : 'text-slate-900'
                        }`}>
                          ₹{txn.balance_after?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="text-sm text-slate-600">
                    Showing {Math.min((currentPage - 1) * pagination.limit + 1, pagination.total)} to {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} transactions
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {[...Array(pagination.pages)].map((_, i) => {
                        const pageNum = i + 1;
                        // Show first, last, current, and neighbors
                        if (
                          pageNum === 1 ||
                          pageNum === pagination.pages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                        ) {
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                              style={currentPage === pageNum ? {backgroundColor: '#00A699'} : {}}
                            >
                              {pageNum}
                            </Button>
                          );
                        } else if (
                          pageNum === currentPage - 2 ||
                          pageNum === currentPage + 2
                        ) {
                          return <span key={pageNum} className="px-2">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === pagination.pages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-slate-500">No transactions found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default WalletTransactions;
